import { Box } from '@chakra-ui/react';
import VFormFieldHorizontal from '@voly/ui-components/src/v2/VFormFieldHorizontal';
import VTooltip from '@voly/ui-components/src/v2/VTooltip';
import { ReactElement, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import { getDecimalPlacesPlaceholder } from 'src/app/project/modules/custom-fields/custom-field-numbers/utils/getDecimalPlacesPlaceholder';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import PointFieldName from './PointFieldName';
import { parseNumericValue } from '../../../utils/numericValue';
import UnitNumberInput from '../UnitNumberInput';
import { getNumericFormat, isNumericFieldLocked } from './numericFieldFormat';

const MIN_VALUE = Number.MIN_SAFE_INTEGER;

const toSavedValue = (value: number | null): string => (value === null ? '' : String(value));

interface PointFieldNumericProps {
  field: TCustomField;
  value: string;
  readOnly: boolean;
  onSave: (value: string) => void;
}

const PointFieldNumeric = ({
  field,
  value,
  readOnly,
  onSave,
}: PointFieldNumericProps): ReactElement => {
  const savedNumber = parseNumericValue(value);
  const [draft, setDraft] = useState<number | null>(savedNumber);
  const [savedValue, setSavedValue] = useState(value);
  const { unit, decimalPlaces, useGrouping } = getNumericFormat(field);
  const locked = isNumericFieldLocked(field);

  if (savedValue !== value) {
    setSavedValue(value);
    setDraft(savedNumber);
  }

  const commit = (next: number | null): void => {
    if (next === savedNumber) {
      return;
    }
    onSave(toSavedValue(next));
  };

  return (
    <VFormFieldHorizontal
      data-testid="point-custom-field-row"
      size="sm"
      label={<PointFieldName field={field} />}
      helperText={field.description}
      errorText={undefined}
      invalid={undefined}
      readOnly={readOnly}
      disabled={locked}
    >
      <VTooltip content={translate('locked_by_admin')} disabled={!locked}>
        <Box data-testid="point-custom-field-control">
          <UnitNumberInput
            value={draft}
            min={MIN_VALUE}
            unit={unit}
            size="sm"
            textAlign="end"
            placeholder={locked ? '' : getDecimalPlacesPlaceholder(decimalPlaces)}
            formatOptions={{
              minimumFractionDigits: decimalPlaces,
              maximumFractionDigits: decimalPlaces,
              useGrouping,
            }}
            readOnly={readOnly}
            locked={locked}
            calculations
            onValueChange={(valueAsNumber) =>
              setDraft(Number.isFinite(valueAsNumber) ? valueAsNumber : null)
            }
            disabled={locked}
            onCommit={commit}
          />
        </Box>
      </VTooltip>
    </VFormFieldHorizontal>
  );
};

export default PointFieldNumeric;
